import { useMemo, useState } from "react";

const OrdersPanel = ({
  orders = [],
  activeOrderTab,
  onChangeTab,
  onOrderAction,
  pendingOrderActions = {},
  orderActionErrors = {},
  printer,
}) => {
  const [searchText, setSearchText] = useState("");
  const handleDeliveredClick = (order, paymentMethod) => {
    onOrderAction(order.orderID, "DELIVERED", paymentMethod);
  };

  const filteredOrders = useMemo(() => {
    if (!searchText.trim()) return orders;

    const text = searchText.toLowerCase();

    return orders.filter((o) => {
      return (
        String(o.orderNumber).includes(text) ||
        o.craverName?.toLowerCase().includes(text)
      );
    });
  }, [orders, searchText]);

  return (
    <div className="h-100">
      {/* Printer Controls */}
      {printer && (
        <div className="d-flex align-items-center gap-2 mb-2 p-2 rounded flex-wrap"
          style={{ backgroundColor: printer.connected ? "#e8f5e9" : "#fff3e0", fontSize: "0.85rem" }}
        >
          <span
            style={{
              width: 10, height: 10, borderRadius: "50%",
              backgroundColor: printer.connected ? "#4caf50" : "#ff9800",
              display: "inline-block",
            }}
          />
          <span className="fw-bold">
            {printer.connected ? "Printer Connected" : "Printer Offline"}
          </span>

          {!printer.connected ? (
            <div className="d-flex gap-1">
              <button className="btn btn-sm btn-outline-dark" onClick={printer.connectAny}>
                Scan All
              </button>
            </div>
          ) : (
            <div className="d-flex gap-1">
              <button className="btn btn-sm btn-success" onClick={printer.testPrint}>
                Test Print
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={printer.disconnect}>
                Disconnect
              </button>
            </div>
          )}

          {printer.connected && (
            <label className="form-check form-switch mb-0 ms-auto d-flex align-items-center gap-1" style={{ cursor: "pointer" }}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={printer.autoPrintEnabled}
                onChange={(e) => printer.setAutoPrintEnabled(e.target.checked)}
              />
              <span>Auto-Print</span>
            </label>
          )}

          {printer.lastError && (
            <span className="text-danger ms-2" style={{ fontSize: "0.78rem" }}>
              {printer.lastError}
            </span>
          )}
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex gap-2 flex-wrap">
          {[
            { key: "ACTIVE", label: "Active" },
            { key: "PICKUP", label: "For Pickup" },
            { key: "DELIVERED", label: "Delivered" },
            { key: "CANCELLED", label: "Cancelled" },
          ].map((tab) => (
            <button
              key={tab.key}
              className={`btn btn-sm ${
                activeOrderTab === tab.key
                  ? "btn-primary"
                  : "btn-outline-secondary"
              }`}
              onClick={() => onChangeTab(tab.key)}
            >
              {tab.label}{" "}
              {activeOrderTab === tab.key ? `- ${filteredOrders.length}` : ""}
            </button>
          ))}
        </div>

        <div className="d-none d-md-flex align-items-center gap-2">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search order or customer..."
            style={{ width: 240 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />


        </div>
      </div>
      <div className="d-block d-md-none mb-3">
        <input
          type="text"
          className="form-control form-control-sm"
          placeholder="Search order or customer..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      {filteredOrders.length === 0 && (
        <p className="text-muted mt-4">No orders found.</p>
      )}

      <div className="row">
        {filteredOrders.map((order, idx) => {
          const isUpdating = Boolean(pendingOrderActions[order.orderID]);

          return (
            <div
              key={order.orderID || idx}
              className="col-xl-3 col-lg-4 col-md-6 mb-3"
            >
              <div className="card h-100">
                <div className="card-body d-flex flex-column">
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h5 className="mb-0 fw-bold">Order: {order.orderNumber}</h5>
                      <small className="text-muted">{order.createdTime}</small>
                    </div>

                    <h6 className="mb-1 fw-bold">{order.kitchenName}</h6>

                    <p className="mb-1 fw-bold">
                      Customer Name: {order.craverName}
                    </p>

                    {order.scheduledSlot && (
                      <span
                        className="badge rounded-pill text-white mb-2 d-inline-block"
                        style={{ fontSize: "0.8rem", padding: "5px 12px", backgroundColor: "#db5800" }}
                      >
                        🕐 {order.scheduledSlot}
                      </span>
                    )}

                    <p className="mb-1 fw-bold">Order Details</p>

                    <ul className="list-unstyled mb-2">
                      {order.orderedItems?.map((item, i) => (
                        <li key={i}>
                          {item.quantity} × {item.itemName}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-3 border-top">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <span
                          className={`badge ${
                            String(order.paymentStatus ?? "").toUpperCase() === "CASH ON DELIVERY"
                              ? "bg-warning text-dark"
                              : "bg-success text-white"
                          }`}
                          style={{ fontSize: "0.8rem", padding: "5px 10px" }}
                        >
                          {String(order.paymentStatus ?? "").toUpperCase() === "CASH ON DELIVERY"
                            ? "Cash"
                            : "Paid"}
                        </span>

                        {printer?.connected && (
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            style={{ fontSize: "0.7rem", padding: "2px 8px" }}
                            title="Print receipt"
                            onClick={() => printer.printOrder(order)}
                          >
                            🖨️ Print
                          </button>
                        )}
                      </div>

                      {activeOrderTab?.toUpperCase() === "ACTIVE" && (
                        <button
                          className="btn btn-sm btn-warning"
                          disabled={isUpdating}
                          onClick={() => onOrderAction(order.orderID, "READY")}
                        >
                          {isUpdating ? (
                            <span className="d-inline-flex align-items-center gap-2">
                              <span
                                className="spinner-border spinner-border-sm"
                                aria-hidden="true"
                              />
                              Updating...
                            </span>
                          ) : (
                            "Mark as Ready"
                          )}
                        </button>
                      )}

                      {activeOrderTab?.toUpperCase() === "PICKUP" &&
                        String(order.paymentStatus ?? "").toUpperCase() !== "CASH ON DELIVERY" && (
                        <button
                          className="btn btn-sm btn-success"
                          disabled={isUpdating}
                          onClick={() => onOrderAction(order.orderID, "DELIVERED")}
                        >
                          {isUpdating ? (
                            <span className="d-inline-flex align-items-center gap-2">
                              <span
                                className="spinner-border spinner-border-sm"
                                aria-hidden="true"
                              />
                              Updating...
                            </span>
                          ) : (
                            "Mark as Delivered"
                          )}
                        </button>
                      )}

                      {activeOrderTab?.toUpperCase() === "PICKUP" &&
                        String(order.paymentStatus ?? "").toUpperCase() === "CASH ON DELIVERY" &&
                        order.CashPaymentMode && (
                        <button
                          className="btn btn-sm btn-success"
                          disabled={isUpdating}
                          onClick={() => handleDeliveredClick(order, order.CashPaymentMode)}
                        >
                          {isUpdating ? (
                            <span className="d-inline-flex align-items-center gap-2">
                              <span
                                className="spinner-border spinner-border-sm"
                                aria-hidden="true"
                              />
                              Updating...
                            </span>
                          ) : (
                            "Mark as Delivered"
                          )}
                        </button>
                      )}
                    </div>

                    {activeOrderTab?.toUpperCase() === "PICKUP" &&
                      String(order.paymentStatus ?? "").toUpperCase() === "CASH ON DELIVERY" &&
                      !order.CashPaymentMode && (
                        <div className="d-flex gap-2 mt-2">
                          <button
                            className="btn btn-sm btn-success text-white flex-fill"
                            style={{ fontSize: "0.78rem", padding: "6px 8px" }}
                            disabled={isUpdating}
                            onClick={() => handleDeliveredClick(order, "ONLINE")}
                          >
                            {isUpdating ? (
                              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                            ) : (
                              "Online Delivered"
                            )}
                          </button>
                          <button
                            className="btn btn-sm btn-warning flex-fill"
                            style={{ fontSize: "0.78rem", padding: "6px 8px" }}
                            disabled={isUpdating}
                            onClick={() => handleDeliveredClick(order, "CASH")}
                          >
                            {isUpdating ? (
                              <span className="spinner-border spinner-border-sm" aria-hidden="true" />
                            ) : (
                              "Cash Delivered"
                            )}
                          </button>
                        </div>
                    )}
                  </div>
                  {isUpdating && (
                    <p
                      className="text-primary mt-2 mb-0 small"
                      role="status"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      Syncing status with server...
                    </p>
                  )}
                  {orderActionErrors[order.orderID] && (
                    <p className="text-danger mt-2 mb-0 small">
                      {orderActionErrors[order.orderID]}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>


    </div>
  );
};

export default OrdersPanel;
